import {
  faArrowUpRightFromSquare,
  faCopy,
  faLock,
  faMessageBot,
  faMobileButton,
  faPaperPlane,
  faShareNodes,
} from '@fortawesome/pro-solid-svg-icons';
import { Button, Label, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@playcastdotio/Shadcn';
import { InviteModule } from '../layout/InviteModule';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useRef, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import clsx from 'clsx';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { isElectron } from '../../api';
import { useClerk } from '@clerk/clerk-react';
import { createRoot } from 'react-dom/client';
import { Privacy } from '@playcastdotio/Settings';
import { Tools } from './Tools';
import { ChatBotShare } from './ChatBotShare';
import constants from '@playcastdotio/GlobalConfig';
import { serviceContainer } from '../../services/ServiceContainer';

interface CustomTooltipProps {
  text: string;
  children: React.ReactNode;
  disableButton: boolean;
}

const CustomTooltip = ({ text, children, disableButton }: CustomTooltipProps) => (
  <TooltipProvider>
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span className={clsx(disableButton ? 'cursor-not-allowed self-end' : '', 'w-full')}>{children}</span>
      </TooltipTrigger>
      {disableButton && (
        <TooltipContent>
          <p>{text}</p>
        </TooltipContent>
      )}
    </Tooltip>
  </TooltipProvider>
);

export const SendInvitePanel = () => {
  return (
    <div className='space-y-4'>
      <Label className='flex h-10 flex-1 items-center font-[Syne] text-xl font-light'>Send Invites</Label>
      <div className='flex gap-4'>
        <InviteButton text='Share' icon={faShareNodes} />
        {/* <InviteButton text='Manage' icon={faUserCrown} /> */}
        <ManageButton />
      </div>
    </div>
  );
};

export const InviteButton = ({
  tooltip,
  icon,
  text,
  position = 'bottom',
  className,
}: {
  tooltip?: string;
  position?: 'top' | 'bottom';
  icon: IconProp;
  text: string;
  className?: string;
}) => {
  const context = useContext(PlaycastContext);
  const [show, setShow] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      context.stateStores.hostState.status === 'streaming' &&
      context.stateStores.invitationStateV2.type === 'lobby' &&
      context.stateStores.invitationStateV2.manifest.lobbyOptions.canJoin
    ) {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [
    context.stateStores.hostState.status,
    context.stateStores.invitationStateV2.manifest,
    context.stateStores.invitationStateV2.type,
    context.stateStores.queueState.enabled,
  ]);

  const handleCopyClicked = () => {
    serviceContainer.analyticsManager.sendInviteCreatedEvent({
      invitationId: context.stateStores.invitationStateV2.id,
      inviteType: 'copy link',
    });

    setShow(false);
    const streamUrl = `${context.stateStores.invitationStateV2.type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${context.stateStores.invitationStateV2.id}`;
    if (context.electronFunctions) {
      context.electronFunctions.copyToClipboard(streamUrl);
    } else {
      navigator.clipboard.writeText(streamUrl);
    }
  };

  const handleSendEmail = (target: string, message: string) => {
    serviceContainer.analyticsManager.sendInviteCreatedEvent({
      invitationId: context.stateStores.invitationStateV2.id,
      inviteType: 'email',
    });
    context.targetFunctions.sendInvitationEmail(target, message);
  };

  const handleSendSms = (target: string, message: string) => {
    serviceContainer.analyticsManager.sendInviteCreatedEvent({
      invitationId: context.stateStores.invitationStateV2.id,
      inviteType: 'sms',
    });
    context.targetFunctions.sendInvitationSms(target, message);
  };

  return (
    <div className='relative flex w-full flex-col gap-2' data-testid='share-invite-button'>
      <CustomTooltip text={tooltip || 'Start a session to invite friends!'} disableButton={disableButton}>
        <div
          className={clsx(
            disableButton ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            'flex h-12 w-full items-center justify-center rounded-lg border-2 border-amber-200 bg-inherit p-2 text-amber-200',
            className,
          )}
          // onClick={() => {
          //   if (disableButton) return;
          //   setShow((prev) => !prev);
          // }}
          onMouseDown={() => {
            if (disableButton) return;
            setShow((prev) => !prev);
            setTimeout(() => {
              dropdownRef.current?.focus();
            }, 0);
          }}
        >
          <div className='flex flex-1 items-center justify-center text-center'>
            <FontAwesomeIcon icon={icon} className='!text-amber-200' />
            <span className={clsx(disableButton ? 'cursor-not-allowed' : 'cursor-pointer', 'pl-2 font-semibold')}>{text}</span>
          </div>
          {/* <FontAwesomeIcon icon={faCaretDown} className='h-3 w-3 transition-all' /> */}
        </div>
      </CustomTooltip>
      <div
        ref={dropdownRef}
        tabIndex={0}
        onBlur={() => setShow(false)}
        className={clsx(
          position === 'bottom' ? 'top-12' : 'bottom-12',
          'absolute z-10 my-2 flex min-w-[180px] flex-col gap-2 rounded-md bg-gray-300 p-2 text-gray-900 shadow',
          show ? 'block' : 'hidden',
        )}
      >
        <button className='group flex items-center' onMouseDown={handleCopyClicked} data-testid='share-copy-link-button'>
          <div
            className='group-hover:border-midnight-bright flex w-full cursor-pointer gap-2 rounded-md border-2 border-gray-300 p-2 text-left text-sm font-normal group-hover:border-dashed'
            data-testid='share-copy-link-button'
          >
            <FontAwesomeIcon icon={faCopy} />
            <Label className='cursor-pointer'>Copy Link</Label>
          </div>
        </button>
        <InviteModule
          title='SMS Invite'
          buttonText='SMS'
          buttonIcon={faMobileButton}
          placeholder='Enter Phone Number'
          onSend={handleSendSms}
          type='sms'
          data-testid='share-sms-button'
        />
        <InviteModule
          title='Email Invite'
          buttonText='Email'
          buttonIcon={faPaperPlane}
          placeholder='email@domain.com'
          onSend={handleSendEmail}
          type='email'
          data-testid='share-email-button'
        />
        <ChatBotShare />
      </div>
    </div>
  );
};

const ManageButton = () => {
  const context = useContext(PlaycastContext);
  const [disableButton, setDisableButton] = useState(false);

  useEffect(() => {
    if (context.stateStores.hostState.status === 'streaming' && context.stateStores.queueState.enabled) {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [context.stateStores.hostState.status, context.stateStores.queueState.enabled]);

  const handleQuickJoin = () => {
    const streamUrl = `${context.stateStores.invitationStateV2.type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${context.stateStores.invitationStateV2.id}`;
    window.open(streamUrl, '_blank', 'noopener,noreferrer');
  };

  // const handleRemoteControl = () => {
  //   const url = 'https://dashboard.playcast.io/';
  //   window.open(url, '_blank', 'noopener,noreferrer');
  // };

  return (
    <div className='flex w-full flex-col gap-2'>
      <CustomTooltip
        text={isElectron ? 'Start playcasting to remote control' : 'Start playcasting to quick join'}
        disableButton={disableButton}
      >
        {isElectron ? (
          ''
        ) : (
          <Button className='embossed-primary m-0 w-full text-sm font-semibold' onClick={handleQuickJoin} disabled={disableButton}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            <span>Quick Join</span>
          </Button>
        )}
      </CustomTooltip>
    </div>
  );
};
